import { useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  ArrowLeft01Icon,
  ArrowRight01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { cx } from "shared/utils";
import boomLogo from "shared/branding/logo.svg";
import { useSurveyStore, selectSortedQuestions } from "../../store";
import { Toolbar } from "./toolbar";
import { ProgressBar } from "./progress-bar";
import { QuestionInput } from "./question-input";
import { SummaryView } from "./summary-view";
import { SlideTransition } from "./slide-transition";
import { isAutoAdvanceType } from "./types";
import type { ViewMode, Answers } from "./types";

export const PreviewModal = ({ onClose }: { onClose: () => void }) => {
  const questions = useSurveyStore(selectSortedQuestions);

  const [viewMode, setViewMode] = useState<ViewMode>("desktop");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [isComplete, setIsComplete] = useState(false);
  const directionRef = useRef<"left" | "right">("right");

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIdx];
  const currentAnswer = answers[currentQuestion.index];
  const isMobile = viewMode === "mobile";

  const canGoNext = (() => {
    if (!currentQuestion.is_required) return true;
    if (!currentAnswer) return false;
    if (Array.isArray(currentAnswer)) return currentAnswer.length > 0;
    return currentAnswer.length > 0;
  })();

  const restart = useCallback(() => {
    directionRef.current = "right";
    setCurrentIdx(0);
    setAnswers({});
    setIsComplete(false);
  }, []);

  const goNext = useCallback(() => {
    directionRef.current = "right";
    if (currentIdx < totalQuestions - 1) setCurrentIdx((i) => i + 1);
    else setIsComplete(true);
  }, [currentIdx, totalQuestions]);

  const goBack = useCallback(() => {
    directionRef.current = "left";
    if (isComplete) setIsComplete(false);
    else if (currentIdx > 0) setCurrentIdx((i) => i - 1);
  }, [currentIdx, isComplete]);

  const handleAnswer = useCallback(
    (value: string | string[]) => {
      setAnswers((prev) => ({ ...prev, [currentQuestion.index]: value }));
      if (isAutoAdvanceType(currentQuestion.type)) goNext();
    },
    [currentQuestion, goNext]
  );

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-white">
      <Toolbar
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onRestart={restart}
        onClose={onClose}
      />

      <div className="flex-grow overflow-hidden bg-background-900 flex items-start justify-center">
        <div
          className={cx(
            "flex flex-col transition-all duration-300",
            isMobile
              ? "w-[375px] my-6 h-[calc(100%-48px)] rounded-2xl border border-alpha-100 shadow-lg bg-white overflow-hidden"
              : "w-full h-full bg-white"
          )}
        >
          <div className={cx("shrink-0", isMobile ? "px-5 py-4" : "px-8 py-4")}>
            <img src={boomLogo} alt="Boom" height={30} />
          </div>

          {!isComplete && (
            <ProgressBar current={currentIdx} total={totalQuestions} />
          )}

          <form
            className="flex flex-col flex-grow overflow-hidden"
            onSubmit={(e) => {
              e.preventDefault();
              if (canGoNext && !isComplete) goNext();
            }}
          >
            <div
              className={cx(
                "flex-grow overflow-hidden bg-background-900 relative",
                isMobile ? "px-5 py-6" : "px-8 py-10"
              )}
            >
              <div
                className={
                  isMobile ? "h-full" : "max-w-[500px] mx-auto w-full h-full"
                }
              >
                {isComplete ? (
                  <div className="overflow-y-auto h-full">
                    <SummaryView questions={questions} answers={answers} />
                  </div>
                ) : (
                  <SlideTransition
                    transitionKey={currentIdx}
                    direction={directionRef.current}
                  >
                    <div className="overflow-y-auto h-full">
                      <h2
                        className={cx(
                          "font-bold text-content-1000 mb-1",
                          isMobile ? "text-lg" : "text-xl"
                        )}
                      >
                        {currentQuestion.question || "Untitled question"}
                      </h2>
                      <Show if={currentQuestion.description}>
                        <p className="text-sm text-content-400 mb-6">
                          {currentQuestion.description}
                        </p>
                      </Show>
                      {!currentQuestion.description && <div className="mb-6" />}
                      <QuestionInput
                        question={currentQuestion}
                        answer={currentAnswer}
                        onAnswer={handleAnswer}
                      />
                    </div>
                  </SlideTransition>
                )}
              </div>
            </div>

            {!isComplete && totalQuestions > 0 && (
              <div
                className={cx(
                  "shrink-0 border-t border-alpha-80",
                  isMobile ? "px-5 py-4 flex gap-3" : "px-8 py-4"
                )}
              >
                <div
                  className={cx(
                    "flex gap-3",
                    isMobile ? "w-full" : "max-w-[500px] mx-auto w-full"
                  )}
                >
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.BASE}
                    HugeIcon={ArrowLeft01Icon}
                    onClick={goBack}
                    disabled={currentIdx === 0}
                    className={isMobile ? "w-[100px] shrink-0" : "flex-1"}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    size={ButtonSize.SM}
                    variant={ButtonVariant.PRIMARY}
                    HugeIcon={ArrowRight01Icon}
                    iconPosition="right"
                    className="flex-1 w-full"
                  >
                    {currentIdx === totalQuestions - 1 ? "Submit" : "Next"}
                  </Button>
                </div>
              </div>
            )}

            {isComplete && (
              <div
                className={cx(
                  "shrink-0 border-t border-alpha-80",
                  isMobile ? "px-5 py-4 flex gap-3" : "px-8 py-4"
                )}
              >
                <div
                  className={cx(
                    "flex gap-3",
                    isMobile ? "" : "max-w-[500px] mx-auto w-full"
                  )}
                >
                  <Button
                    size={ButtonSize.SM}
                    variant={ButtonVariant.PRIMARY}
                    onClick={onClose}
                    className="flex-1"
                  >
                    Done
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};
